// src/components/custom/PledgeBlock.jsx
//
// A statement-with-commitments block (a pledge, a code, a charter) that a
// topic page can carry ABOVE its modules. Reads the same `landing` map that
// CustomLandingView renders as a full page, so content authored for one works
// in the other: Hockey Alberta's Player Pledge started as its own page and
// now lives at the top of Respect & Belonging, with their materials below it.
//
// Renders nothing when the map has no statement or commitments, so a topic
// doc without one is unaffected.
//
// An action with `embed: true` opens its form INSIDE the page (an iframe under
// the buttons) instead of a new tab, so a player signs the pledge without
// leaving the hub. A monday.com WorkForm link is turned into its embed address
// here, so the public form link is all the content needs to carry.
//
// An action may also carry `prefill`: a map of the form's question ids to
// values, where a value may be a placeholder filled from the `context` prop
// ({team}, {division}, {association}). Hockey Alberta's forms take their
// Division, Team Name and Association questions this way (verified 2026-10-27
// on both forms), so a player never retypes their team. Only the team's
// facts are carried, never the member's name or email.

import React, { useState } from 'react';
import { Quote, ArrowRight, ExternalLink, Printer, X } from 'lucide-react';

// An https url or a site-relative path (a poster, a PDF) opens in a new tab.
const isExternal = (target) => /^(https?:\/\/|\/)/i.test(target || '');

// forms.monday.com/forms/<id> -> forms.monday.com/forms/embed/<id>; anything
// else is embedded as given (a form host that allows framing).
export const embedUrlFor = (target) => {
    const t = String(target || '').trim();
    const m = /^(https:\/\/forms\.monday\.com\/forms\/)(?!embed\/)([^?#]+)(\?.*)?$/i.exec(t);
    return m ? `${m[1]}embed/${m[2]}${m[3] || ''}` : t;
};

// The target with the action's prefill appended. A placeholder that resolves
// to nothing is left out, so the question simply stays empty.
export const prefilledUrl = (target, prefill, context = {}) => {
    const t = String(target || '').trim();
    if (!t || !prefill || typeof prefill !== 'object') return t;
    const fill = (v) => String(v == null ? '' : v).replace(/\{(\w+)\}/g, (_, k) => (context[k] == null ? '' : String(context[k])));
    const params = Object.entries(prefill)
        .map(([k, v]) => [k, fill(v).trim()])
        .filter(([k, v]) => k && v);
    if (!params.length) return t;
    const [base, hash] = t.split('#');
    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}${params.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}${hash ? `#${hash}` : ''}`;
};

const PledgeBlock = ({ landing, onNavigate, onAction = null, context = {} }) => {
    // The action whose form is open inline, if any.
    const [openForm, setOpenForm] = useState(null);
    if (!landing) return null;
    const commitments = Array.isArray(landing.commitments) ? landing.commitments : [];
    const steps = Array.isArray(landing.steps) ? landing.steps : [];
    const actions = Array.isArray(landing.actions) ? landing.actions : [];
    if (!landing.statement && commitments.length === 0) return null;

    // An external target carries the action's prefill (the member's team facts).
    const resolved = (a) => (isExternal(a.target) ? prefilledUrl(a.target, a.prefill, context) : a.target);
    const run = (target, action = null) => {
        if (!target) return;
        if (onAction && action) onAction(action);
        if (action && action.embed && isExternal(target)) { setOpenForm({ ...action, target: resolved(action) }); return; }
        if (isExternal(target)) window.open(action ? resolved(action) : target, '_blank', 'noopener,noreferrer');
        else if (onNavigate) onNavigate(target);
    };

    return (
        <div className="rounded-3xl border border-slate-200 p-6 md:p-8" style={{ backgroundColor: 'var(--brand-highlight)', borderLeft: '6px solid var(--brand-accent)' }}>
            {(landing.eyebrow || landing.headline) && (
                <div className="mb-4">
                    {landing.eyebrow && <p className="text-[11px] font-black uppercase tracking-widest text-brand-primary mb-1">{landing.eyebrow}</p>}
                    {landing.headline && <h2 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">{landing.headline}</h2>}
                    {landing.tagline && <p className="text-sm font-bold text-slate-500 mt-1">{landing.tagline}</p>}
                </div>
            )}
            {landing.intro && <p className="text-sm text-slate-600 font-medium leading-relaxed mb-4">{landing.intro}</p>}

            {landing.statement && (
                <p className="text-lg md:text-xl font-black text-slate-900 leading-snug flex items-start gap-3">
                    <Quote size={22} className="text-brand-primary shrink-0 mt-1" />
                    <span>{landing.statement}</span>
                </p>
            )}
            {commitments.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5">
                    {commitments.map((c, i) => (
                        <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5">
                            {c.keyword && <p className="text-xl font-black text-brand-primary tracking-tight">{c.keyword}</p>}
                            <p className="text-sm text-slate-600 font-medium leading-relaxed mt-1">{c.text}</p>
                        </div>
                    ))}
                </div>
            )}

            {steps.length > 0 && (
                <div className="mt-6">
                    <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">{landing.stepsTitle || 'How to take part'}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {steps.map((s, i) => (
                            <div key={i} className="flex gap-3 items-start px-4 py-3 bg-white rounded-2xl border border-slate-200">
                                <div className="w-7 h-7 rounded-xl bg-brand-light text-brand-primary flex items-center justify-center font-black shrink-0 text-xs mt-0.5">{i + 1}</div>
                                <div>
                                    <p className="font-bold text-slate-800 text-sm leading-tight">{s.title}</p>
                                    {s.text && <p className="text-sm text-slate-500 leading-relaxed mt-0.5">{s.text}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* The printable poster — "put the Pledge on your door" starts here. */}
            {landing.posterUrl && (
                <a
                    href={landing.posterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 mt-6 p-3 pr-5 bg-white rounded-2xl border border-slate-200 hover:border-brand-primary/40 hover:shadow-sm transition-all"
                >
                    <img src={landing.posterUrl} alt={landing.headline || 'Poster'} className="w-28 sm:w-36 rounded-xl border border-slate-100 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-sm font-black text-slate-900 leading-snug">Put the Pledge on your door</p>
                        <p className="text-xs text-slate-500 font-medium leading-relaxed mt-1">Print the poster, have the team sign it, and keep it where everyone sees it each time they come together.</p>
                        <span className="inline-flex items-center gap-1.5 mt-2 text-xs font-black text-brand-primary group-hover:underline underline-offset-2"><Printer size={13} /> Open the poster to print</span>
                    </div>
                </a>
            )}

            {actions.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-6">
                    {actions.map((a, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => run(a.target, a)}
                            className={`px-5 py-3 rounded-full font-black text-sm flex items-center gap-2 transition-colors ${
                                a.primary ? 'bg-brand-primary text-white hover:bg-brand-dark shadow-lg shadow-brand-primary/25' : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
                            }`}
                        >
                            {a.label}
                            {isExternal(a.target) && !a.embed ? <ExternalLink size={15} /> : <ArrowRight size={15} />}
                        </button>
                    ))}
                </div>
            )}

            {openForm && (
                <div className="mt-5 bg-white rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
                        <p className="text-sm font-black text-slate-900">{openForm.label}</p>
                        <div className="flex items-center gap-3">
                            <a href={openForm.target} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-brand-primary hover:underline inline-flex items-center gap-1">Open in a new tab <ExternalLink size={12} /></a>
                            <button type="button" onClick={() => setOpenForm(null)} className="p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Close"><X size={16} /></button>
                        </div>
                    </div>
                    <iframe
                        title={openForm.label}
                        src={embedUrlFor(openForm.target)}
                        className="w-full border-0"
                        style={{ height: Number(openForm.embedHeight) || 900 }}
                        allow="clipboard-write"
                    />
                </div>
            )}
        </div>
    );
};

export default PledgeBlock;
